import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { PrefetchLinkDirective, SpinnerComponent } from '@kaseya-design-components';
import { TopNavigationPath } from '@core/navigation/models/top-navigation/top-navigation-path';
import { NavigationFacade } from '@core/navigation/facades/navigation.facade';

import { SsoTargetPage } from '@design/open-sso-link/models/sso-target-page.enum';
import { SsoSalesforceDestination } from '@design/open-sso-link/models/sso-salesforce-destination.enum';
import { BehaviorSubject } from 'rxjs';
import { AsyncPipe, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { FeaturesDirective } from '@core/features/directives/features.directive';
import { PermissionDirective } from '@core/permissions/directives/permission.directive';
import { Permission } from '@core/permissions/models/permission.enum';
import { OpenSsoLinkDirective } from '@design/open-sso-link/directives/open-sso-link.directive';
import { OpenSsoSalesforceLinkDirective } from '@design/open-sso-link/directives/open-sso-salesforce-link.directive';
import { CountryAccessDirective } from '@core/locations/directives/country-access.directive';
import { TopNavigationDropdownComponent } from '@layout/navigation/components/top-navigation-dropdown/top-navigation-dropdown.component';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { AuthorizationService } from '@core/authorization/services/authorization.service';

@Component({
  selector: 'itc-top-navigation',
  templateUrl: 'top-navigation.component.html',
  styleUrl: 'top-navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    PermissionDirective,
    RouterLink,
    PrefetchLinkDirective,
    RouterLinkActive,
    SpinnerComponent,
    OpenSsoLinkDirective,
    OpenSsoSalesforceLinkDirective,
    AsyncPipe,
    FeaturesDirective,
    CountryAccessDirective,
    SlicePipe,
    NgTemplateOutlet,
    TopNavigationDropdownComponent,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
  ],
})
export class TopNavigationComponent implements AfterViewInit, OnDestroy {
  private readonly navigationFacade = inject(NavigationFacade);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly resizeObserver = new ResizeObserver(() => this.setVisibleTopNavigationItemsCount());
  private readonly mutationObserver = new MutationObserver(() => this.setVisibleTopNavigationItemsCount());
  private readonly elementRef = inject(ElementRef);

  readonly navigationItemsPanelElement = viewChild.required<ElementRef>('navigationItemsPanelElement');
  readonly hiddenOverlay = viewChild.required<ElementRef<HTMLDivElement>>('hiddenOverlay');
  readonly allNavigationItemElements = viewChildren<ElementRef<HTMLElement>>('navItem');
  readonly navigationItemsElements = computed(() => {
    const container = this.hiddenOverlay().nativeElement;
    return this.allNavigationItemElements().filter(({ nativeElement }) => container.contains(nativeElement));
  });
  readonly navigationItems = this.navigationFacade.topNavigationItems;
  readonly accessibleNavigationItems = computed(() => {
    const allowedIds = new Set(
      this.navigationItemsElements()
        .map(({ nativeElement }) => nativeElement.getAttribute('data-nav-id'))
        .filter((id): id is string => Boolean(id))
    );
    return this.navigationItems().filter(item => allowedIds.has(item.id));
  });
  readonly visibleTopNavigationItemsCount = signal(0);
  readonly isDropdownOpen = signal(false);
  readonly TopNavigationPath = TopNavigationPath;
  readonly SsoTargetPage = SsoTargetPage;
  readonly SsoSalesforceDestination = SsoSalesforceDestination;
  readonly Permission = Permission;
  readonly isKaseyaStoreSsoLinkLoading$ = new BehaviorSubject(false);

  @HostListener('window:resize') onResize(): void {
    this.checkIfOneMoreItemCanFit();
  }

  constructor() {
    this.observeNavigationItemsChanges();
  }

  ngAfterViewInit(): void {
    this.resizeObserver.observe(this.navigationItemsPanelElement().nativeElement);
    this.mutationObserver.observe(this.navigationItemsPanelElement().nativeElement, {
      subtree: true,
      childList: true,
    });

    this.setInitialVisibleTopNavigationItemsCount();
  }

  get isMigratedUser(): boolean {
    return this.authorizationService.isMigratedUser();
  }

  setActiveTopNavigation(id: TopNavigationPath, isActive: boolean): void {
    if (isActive && id !== this.navigationFacade.activeTopNavigation()) {
      this.navigationFacade.setActiveTopNavigation(id);
    }
  }

  toggleDropdown(): void {
    this.isDropdownOpen.update(isOpen => !isOpen);
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    this.mutationObserver.disconnect();
  }

  private observeNavigationItemsChanges(): void {
    effect(() => {
      if (this.navigationItemsElements().length > 0) {
        this.checkIfOneMoreItemCanFit();
      }
    });
  }

  private setVisibleTopNavigationItemsCount(): void {
    if (this.hasScroll) {
      this.visibleTopNavigationItemsCount.update(count => count - 1);
    }
  }

  private checkIfOneMoreItemCanFit(): void {
    const visibleItemsCount = this.visibleTopNavigationItemsCount();
    const elementWidth: number = this.elementRef.nativeElement.clientWidth;
    const itemsPanelWidth: number = this.navigationItemsPanelElement().nativeElement.clientWidth;
    const childElementWidth: number = this.navigationItemsElements()[visibleItemsCount]?.nativeElement?.offsetWidth || 0;

    if (this.navigationItemsElements().length - visibleItemsCount >= 1 && itemsPanelWidth + childElementWidth <= elementWidth) {
      this.visibleTopNavigationItemsCount.update(count => count + 1);
    }
  }

  private setInitialVisibleTopNavigationItemsCount(): void {
    this.visibleTopNavigationItemsCount.set(this.navigationItemsElements().length);
  }

  private get hasScroll(): boolean {
    const { clientWidth, scrollWidth } = this.navigationItemsPanelElement().nativeElement;

    return scrollWidth > clientWidth;
  }
}
